import type { OrgSessionData } from '@strength/server-auth';
import { DateTime } from 'luxon';
import { environment } from '../environment';

/**
 * Bump to force a full bootstrap sync for every user.
 * v18: tablet bootstrap bounded by date window (SC-5518).
 * v19: users who switched roles (e.g. all-access -> coach-admin ->
 * all-access) have local caches whose sync checkpoint advanced past actions
 * the server filtered out while their access was narrower, so those rows
 * never arrive via delta sync (SC-5486).
 */
const CLIENT_DB_VERSION = 19;

/**
 * The IndexedDB name for a user's loco-sync cache. A new name means a fresh
 * database and a full re-bootstrap.
 *
 * The name must change whenever the data the server will let the client see
 * changes: bootstrap and delta payloads are filtered by role and team scope
 * (visibleSyncModels / fetchAllowedSyncGroups), but the client's sync
 * checkpoint advances past filtered-out actions all the same. Without a reset,
 * anything the user could not see in between is permanently missing from the
 * cache (SC-5486). `role` and `teamIds` cover role switches and org/team
 * scope changes: a permission-driven role's scope flip lands in `teamIds`
 * (null for org scope, the membership list for team scope).
 *
 * Unrelated inputs must NOT change the name: the database is still reset
 * weekly on Sundays and on deploy-time schema hash changes.
 */
export function clientDbName(
  auth: Pick<OrgSessionData, 'profileId' | 'orgId' | 'role' | 'teamIds'>,
  now: DateTime = DateTime.now(),
): string {
  // Sunday-based week of the year (0-51 or 0-52 in some years)
  const weekOfTheYear = Math.max(
    0,
    Math.floor((now.ordinal - (now.weekday % 7)) / 7),
  );

  // Team scope stamp: stable regardless of membership ordering. `null` means
  // org-wide access (all-access, or a permission-driven role with org scope).
  const teamScope =
    auth.teamIds === null ? 'all' : [...auth.teamIds].sort().join(',');

  return [
    auth.profileId,
    auth.orgId,
    environment.VITE_DB_HASH,
    `w${weekOfTheYear}`,
    `v${CLIENT_DB_VERSION}`,
    auth.role,
    teamScope,
  ].join('|');
}
